import { Link, useParams } from 'react-router-dom';
import { useState } from 'react';

import useFetch from '../../hooks/useFetch';
import { getProduct } from '../../services/api';
import ProductImage from '../../components/ProductImage';
import RichText from '../../components/RichText';
import Button from '../../components/Button';
import CtaBanner from '../../components/CtaBanner';
import Loader from '../../components/Loader';
import { useForms } from '../../context/FormsContext';

/* ------------------------------------------------------------------ */
/* Stock status helpers                                                  */
/* ------------------------------------------------------------------ */

/**
 * Returns { label, color } for the `.stock` element.
 * Matches the mockup's green dot + descriptive text pattern.
 */
function stockInfo(stockStatus) {
  switch (stockStatus) {
    case 'InStock':
      return { label: 'In Stock · Ships in 1–2 business days', color: 'var(--green)', dotColor: 'var(--green)' };
    case 'LowStock':
      return { label: 'Low Stock · Order soon', color: '#D97706', dotColor: '#D97706' };
    case 'OutOfStock':
      return { label: 'Out of Stock', color: '#DC2626', dotColor: '#DC2626' };
    case 'Discontinued':
      return { label: 'Discontinued', color: 'var(--gray)', dotColor: 'var(--gray)' };
    default:
      return { label: 'Check Availability', color: 'var(--gray)', dotColor: 'var(--gray)' };
  }
}

/** Returns the badge label for the gallery-tag (same as listing). */
function galleryTagLabel(badge, stockStatus) {
  switch (badge) {
    case 'BestSeller':  return 'Best Seller';
    case 'LowBSR':      return 'Low BSR';
    case 'NewArrival':  return 'New Arrival';
    case 'Clearance':   return 'Clearance';
    default:
      if (stockStatus === 'InStock')  return 'In Stock';
      if (stockStatus === 'LowStock') return 'Low Stock';
      return null;
  }
}

/* ------------------------------------------------------------------ */
/* Not-found / error state                                              */
/* ------------------------------------------------------------------ */
function ProductNotFound() {
  return (
    <main>
      <div className="product-page">
        <div className="breadcrumb">
          <span className="sep">/</span>
          <Link to="/products">Products</Link>
          <span className="sep">/</span>
          <span className="current">Not Found</span>
        </div>
        <div className="error-state" style={{ paddingTop: 0 }}>
          <h3>Product not found</h3>
          <p>This product doesn't exist or may have been removed.</p>
          <Link to="/products" className="btn-primary" style={{ marginTop: 20, display: 'inline-block' }}>
            Browse All Products
          </Link>
        </div>
      </div>
    </main>
  );
}

/* ------------------------------------------------------------------ */
/* Main component                                                        */
/* ------------------------------------------------------------------ */
function ProductDetail() {
  const { slug } = useParams();
  const { openQuote } = useForms();
  const [activeImageId, setActiveImageId] = useState(null);

  const { data: product, loading, error } = useFetch(
    () => getProduct(slug),
    [slug]
  );

  if (loading) {
    return (
      <main>
        <div className="product-page">
          <div style={{ paddingTop: 100 }}>
            <Loader />
          </div>
        </div>
      </main>
    );
  }

  if (error || !product) {
    return <ProductNotFound />;
  }

  /* ---- Derived values ---- */
  const category = product.category || {};

  /* All images, ordered. Primary (or first) is the default selection. */
  const sortedImages = (product.images || []).slice().sort((a, b) => a.display_order - b.display_order);
  const primaryImage = sortedImages.find((i) => i.is_primary) ?? sortedImages[0] ?? null;
  const activeImage =
    sortedImages.find((i) => i.id === activeImageId) ?? primaryImage;
  const media = activeImage?.media ?? null;
  const emoji = category.icon ?? '📦';

  const tagLabel = galleryTagLabel(product.badge, product.stock_status);
  const { label: stockLabel, color: stockColor, dotColor } = stockInfo(product.stock_status);

  const wholesalePrice = `$${Number(product.wholesale_price).toFixed(2)}`;
  const msrp = product.msrp ? `$${Number(product.msrp).toFixed(2)}` : null;

  const descriptionHtml = product.long_description || null;
  const descriptionPlain = product.short_description || '';

  return (
    <main>
      <div className="product-page">
        {/* BREADCRUMB */}
        <nav className="breadcrumb" aria-label="Breadcrumb">
          <span className="sep">/</span>
          <Link to="/products">Products</Link>
          {category.slug && (
            <>
              <span className="sep">/</span>
              <Link to={`/products?cat=${category.slug}`}>{category.name}</Link>
            </>
          )}
          <span className="sep">/</span>
          <span className="current">{product.name}</span>
        </nav>

        {/* PRODUCT TOP — 2-column */}
        <div className="product-top">
          {/* LEFT: gallery */}
          <div className="gallery">
            <div className="gallery-main">
              {tagLabel && <span className="gallery-tag">{tagLabel}</span>}
              <ProductImage media={media} emoji={emoji} alt={product.name} />
            </div>

            {sortedImages.length > 1 && (
              <div className="gallery-thumbs">
                {sortedImages.map((img) => (
                  <button
                    key={img.id}
                    type="button"
                    className={`gallery-thumb${
                      (activeImage?.id ?? primaryImage?.id) === img.id ? ' active' : ''
                    }`}
                    onClick={() => setActiveImageId(img.id)}
                    aria-label={`View image ${img.display_order + 1}`}
                  >
                    <ProductImage media={img.media} emoji={emoji} alt={product.name} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* RIGHT: info */}
          <div>
            <div className="p-brand">
              {product.brand}
              {category.name ? ` · ${category.name}` : ''}
            </div>

            <h1 className="p-title">{product.name}</h1>

            <div className="price-now">
              {wholesalePrice} <small>/ unit (wholesale)</small>
            </div>

            {msrp && (
              <div className="price-msrp">
                MSRP <s>{msrp}</s>
              </div>
            )}

            <div className="stock" style={{ color: stockColor }}>
              <span className="dot" style={{ background: dotColor }} />
              {stockLabel}
            </div>

            {/* FACTS BOX */}
            <div className="facts">
              <div className="fact">
                <span className="k">SKU</span>
                <span className="v">{product.sku}</span>
              </div>
              <div className="fact">
                <span className="k">Category</span>
                <span className="v">{category.name || '—'}</span>
              </div>
              <div className="fact">
                <span className="k">Case Pack</span>
                <span className="v">{product.case_pack} units</span>
              </div>
              <div className="fact">
                <span className="k">Minimum Order (MOQ)</span>
                <span className="v">{product.moq} units</span>
              </div>
            </div>

            {/* ACTIONS */}
            <div className="product-actions">
              <Button variant="primary" to="/wholesale">
                Apply for Wholesale
              </Button>
              <Button
                variant="outline-dark"
                onClick={() => openQuote({ productId: product.id, productName: product.name })}
              >
                Request a Quote
              </Button>
            </div>
          </div>
        </div>

        {/* DESCRIPTION */}
        <div className="desc">
          <h2>Description</h2>
          {descriptionHtml ? (
            <RichText html={descriptionHtml} />
          ) : (
            <p>{descriptionPlain}</p>
          )}
        </div>
      </div>

      {/* BOTTOM CTA BANNER */}
      <CtaBanner
        headline="Want Wholesale Pricing on This Product?"
        sub="Approved accounts unlock wholesale pricing, LOA, and compliant invoices — get approved in 24–48 hours."
        primaryLabel="Apply for Wholesale"
        primaryTo="/wholesale"
        outlineLabel="Call: (213) 874-8192"
        outlineHref="tel:+12138748192"
      />
    </main>
  );
}

export default ProductDetail;
